import { VariantProps } from "class-variance-authority";
import React, { createContext, useContext, useRef, useState } from "react";
import { Outlet } from "react-router-dom";

import { buttonVariants } from "@/components/ui/button";
import { ConfirmationModal, ConfirmationModalVariant } from "@/components/ConfirmationModal";

export interface ConfirmParams {
  message?: string | React.ReactNode;
  title?: string | React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  headerVariant?: ConfirmationModalVariant;
  exitButtonVariant?: VariantProps<typeof buttonVariants>["variant"];
  saveButtonVariant?: VariantProps<typeof buttonVariants>["variant"];
  requireConfirmation?: boolean;
  confirmationText?: string;
  confirmationPlaceholder?: string;
}

interface ContextType {
  confirm: (params?: ConfirmParams) => Promise<boolean>;
}

const ConfirmContext = createContext<ContextType | null>(null);

const resolveConfirmDescription = (
  message: ConfirmParams["message"],
): { description?: string; MarkdownDescription?: React.ReactNode } => {
  if (message === undefined) {
    return { description: "This action cannot be undone." };
  }
  if (typeof message === "string") {
    return { description: message };
  }
  return { MarkdownDescription: message };
};

const ConfirmProvider = () => {
  const ref = useRef<(value: boolean) => void>();
  const settledRef = useRef(false);
  const [open, setOpen] = useState(false);
  const [params, setParams] = useState<ConfirmParams>();
  const resolveConfirm = (value: boolean) => {
    if (settledRef.current) {
      return;
    }
    settledRef.current = true;
    const resolve = ref.current;
    ref.current = undefined;
    resolve?.(value);
  };

  const confirm = (confirmParams?: ConfirmParams): Promise<boolean> => {
    settledRef.current = false;
    setParams(confirmParams);
    setOpen(true);
    return new Promise(resolve => {
      ref.current = resolve;
    });
  };

  const handleConfirm = () => {
    resolveConfirm(true);
    setOpen(false);
  };

  const handleCancel = () => {
    resolveConfirm(false);
    setOpen(false);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      resolveConfirm(false);
    }
    setOpen(nextOpen);
  };

  const confirmBody = resolveConfirmDescription(params?.message);

  return (
    <ConfirmContext.Provider value={{ confirm }}>
      <Outlet />
      <ConfirmationModal
        isOpen={open}
        setIsOpen={handleOpenChange}
        title={params?.title || "Are you sure?"}
        description={confirmBody.description}
        MarkdownDescription={confirmBody.MarkdownDescription}
        leaveButtonText={params?.cancelText || "Cancel"}
        saveButtonText={params?.confirmText || "Confirm"}
        headerVariant={params?.headerVariant || ConfirmationModalVariant.warning}
        saveButtonVariant={params?.saveButtonVariant || "default"}
        exitButtonVariant={params?.exitButtonVariant || "destructiveOutline"}
        handleOnConfirm={handleConfirm}
        handleLeave={handleCancel}
        useBlocker={false}
        requireConfirmation={params?.requireConfirmation}
        confirmationText={params?.confirmationText}
        confirmationPlaceholder={params?.confirmationPlaceholder}
      />
    </ConfirmContext.Provider>
  );
};

const useConfirm = () => {
  const context = useContext(ConfirmContext);
  if (!context) {
    throw new Error("useConfirm must be used within a ConfirmProvider");
  }
  return context;
};

export { ConfirmProvider, useConfirm };
